import type { AxiosError } from 'axios';
import type { RunConfigResponse } from '@xpanse/ui';

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router';

import { usePreviousLocation } from 'src/routes/hooks/usePreviousLocation';

import { RunConfigService } from 'src/utils/api';
import { getParentRoute } from 'src/utils/navigation';
import { API_ERROR_MESSAGES } from 'src/utils/constants';

import { toast } from 'src/components/snackbar';

export const getRunConfig = async (runConfigId: string): Promise<RunConfigResponse | null> => {
  if (!runConfigId) {
    throw new Error('Run Config ID is required');
  }
  const response = await RunConfigService.getRunConfig(runConfigId);

  // Note: Previously, we checked for HTTP status code 200.
  // Now, since the API service directly returns the data object,
  // `response` itself represents the result, and `response.status`
  // reflects the internal run status, not an HTTP code.
  // if (response.status !== ApiRunConfigStatus.Completed) {
  //   return null;
  // }

  return response;
};

export const useRunConfig = (runConfigId: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { prevLocation } = usePreviousLocation();

  const { data, isPending, error } = useQuery({
    queryKey: ['run-config', runConfigId],
    queryFn: () => getRunConfig(runConfigId),
    enabled: !!runConfigId,
  });

  useEffect(() => {
    const _error = error as AxiosError;

    const handleNavigation = (errorMessage: string) => {
      toast.error(errorMessage);

      const parentRoute = getParentRoute(location.pathname);
      if (prevLocation) {
        navigate(-1);
      } else {
        navigate(parentRoute);
      }
    };

    if (_error?.status === 400) {
      handleNavigation(API_ERROR_MESSAGES.NOT_FOUND_RUN);
    } else if (_error?.status === 500) {
      handleNavigation(API_ERROR_MESSAGES.FAIL_TO_FETCH_RUN);
    }
  }, [error, navigate, location.pathname, prevLocation]);

  return {
    data,
    isPending,
  };
};
